import React from 'react';
import { Formik } from 'formik';
import type { FormikProps } from 'formik';
import { object, string, number } from 'yup';
import {
  Form,
  FormItem,
  Input,
  Select as FormikSelect,
  InputNumber,
} from 'formik-antd';
import { Button, Form as AntForm, Select as AntSelect } from 'antd';
import { FormikOnSubmit } from 'lib/formik.utils';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { v4 } from 'uuid';
import { useQuery } from 'react-query';
import {
  partnerNameValidationSchema,
  partnerUniqueNumberValidationSchema,
} from 'common/config/validationSchema';
import { useRole } from 'common/hooks/useRole';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { QueryKey } from 'api/queryKey';
import {
  PartnerModel,
  partner_models,
  SubscriptionPlan,
  subscription_plans,
} from 'models/businessPartner';

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 14 },
};
const tailLayout = {
  wrapperCol: { offset: 8, span: 14 },
};

export interface BusinessPartnerFormModel {
  partnerModel?: PartnerModel;
  name: string;
  uniqueNumber: string;
  subscription?: SubscriptionPlan;
  examLimit?: number | null;
  description?: string;
}

export interface BusinessPartnerFormProps {
  onSubmit: FormikOnSubmit<any>;
  loading?: boolean;
  value?: BusinessPartnerFormModel;
  readonly?: boolean;
  isSubpartnerForm?: boolean;
  bpContextID?: number;

  hideInternalSubmit?: boolean;
  formikRef?: React.Ref<FormikProps<any>>;

  lockIdentityFields?: boolean;
}

export const BusinessPartnerForm = ({
  onSubmit,
  loading = false,
  value,
  readonly = false,
  isSubpartnerForm = false,
  bpContextID,
  hideInternalSubmit = false,
  formikRef,
  lockIdentityFields = false,
}: BusinessPartnerFormProps) => {
  const intlMessage = useFormatMessage();

  const generetedUUID = bpContextID ? `${bpContextID}-${v4()}` : v4();
  const { isSuperAdmin } = useRole();

  const { data: partnerModelsResponse } = useQuery(
    [QueryKey.BusinessPartnerPlans],
    () => BusinessPartnersAPI.fetchBusinessPartnerPlans(),
    {
      enabled: isSuperAdmin,
      keepPreviousData: true,
    }
  );

  const availablePartnerModels: string[] =
    partnerModelsResponse && partnerModelsResponse.length > 0
      ? partnerModelsResponse
      : [...partner_models];

  const validationPartnerModels = value?.partnerModel
    ? Array.from(new Set([...availablePartnerModels, value.partnerModel]))
    : availablePartnerModels;

  const initialValues: BusinessPartnerFormModel = {
    partnerModel: 'STANDARD',
    name: '',
    uniqueNumber: !isSubpartnerForm ? '' : generetedUUID,
    subscription: undefined,
    examLimit: null,
    description: '',
  };

  const validationSchema = object().shape({
    partnerModel: string().oneOf(validationPartnerModels).nullable(),
    name: partnerNameValidationSchema(intlMessage),
    uniqueNumber: partnerUniqueNumberValidationSchema(intlMessage),
    description: partnerUniqueNumberValidationSchema(intlMessage),
    subscription: string().oneOf(subscription_plans).nullable(),
    examLimit: number()
      .transform((curr, orig) => {
        if (orig === '' || orig === null || typeof orig === 'undefined')
          return null;
        return Number.isNaN(curr) ? null : curr;
      })
      .integer(intlMessage({ id: 'validation.mustBeInteger' }))
      .min(0, intlMessage({ id: 'validation.mustBeAtLeast' }, { min: 0 }))
      .nullable(),
  });

  const handleSubmit: FormikOnSubmit<BusinessPartnerFormModel> = (
    values,
    helpers
  ) => {
    const { subscription, examLimit, partnerModel, ...rest } = values;

    const subscriptionConfig: Record<string, unknown> = {};
    if (subscription) subscriptionConfig.subscriptionPlan = subscription;
    if (examLimit !== null && typeof examLimit !== 'undefined') {
      subscriptionConfig.customExaminationsLimit = examLimit;
    }

    const payload: any = {
      ...rest,
      ...(partnerModel ? { partnerType: partnerModel } : {}),
      ...(Object.keys(subscriptionConfig).length ? { subscriptionConfig } : {}),
    };

    return onSubmit(payload, helpers);
  };

  const identityDisabled = lockIdentityFields;
  const getPartnerModelOptionLabel = (model: PartnerModel) =>
    intlMessage({ id: `partnerModel.option.${model}` });

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={value ?? initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      innerRef={(instance) => {
        if (!formikRef) return;
        if (typeof formikRef === 'function') {
          formikRef(instance as FormikProps<any>);
        } else {
          (formikRef as React.MutableRefObject<FormikProps<
            any
          > | null>).current = instance as FormikProps<any>;
        }
      }}
    >
      {({ setFieldValue, values }) => (
        <Form {...layout} style={{ margin: '50px 0 50px 0' }}>
          <FormItem
            label={intlMessage({ id: 'partnerModel' })}
            name="partnerModel"
            required={false}
          >
            <AntSelect
              value={values.partnerModel}
              disabled={!isSuperAdmin || readonly}
              onChange={(value: PartnerModel) =>
                setFieldValue('partnerModel', value)
              }
            >
              {availablePartnerModels.map((model) => (
                <AntSelect.Option key={model} value={model}>
                  {getPartnerModelOptionLabel(model as PartnerModel)}
                </AntSelect.Option>
              ))}
            </AntSelect>
          </FormItem>

          <FormItem
            label={intlMessage({ id: 'name' })}
            name="name"
            required={!identityDisabled}
          >
            <Input
              name="name"
              readOnly={identityDisabled}
              disabled={identityDisabled}
            />
          </FormItem>

          {!isSubpartnerForm && (
            <FormItem
              label={intlMessage({ id: 'identifier' })}
              name="uniqueNumber"
              required={!identityDisabled}
            >
              <Input
                name="uniqueNumber"
                readOnly={identityDisabled}
                disabled={identityDisabled}
              />
            </FormItem>
          )}

          {isSubpartnerForm && (
            <FormItem
              label="UUID"
              name="uniqueNumber"
              required={false}
              style={{ display: 'none' }}
            >
              <Input name="uniqueNumber" readOnly />
            </FormItem>
          )}

          {isSubpartnerForm && (
            <FormItem
              label={intlMessage({ id: 'identifier' })}
              name="description"
              required={false}
            >
              <Input name="description" readOnly disabled />
            </FormItem>
          )}

          <FormItem
            label={intlMessage({ id: 'subscriptionPlan' })}
            name="subscription"
            required={false}
          >
            <FormikSelect
              name="subscription"
              disabled={!isSuperAdmin || readonly}
              allowClear={false}
            >
              {subscription_plans.map((plan) => (
                <FormikSelect.Option key={plan} value={plan}>
                  {plan}
                </FormikSelect.Option>
              ))}
            </FormikSelect>
          </FormItem>

          <FormItem
            label={intlMessage({ id: 'examLimit' })}
            name="examLimit"
            required={false}
          >
            <InputNumber
              name="examLimit"
              disabled={!isSuperAdmin || readonly}
              min={0}
              step={1}
              precision={0}
              style={{ width: '100%' }}
              onChange={(val) =>
                setFieldValue('examLimit', typeof val === 'number' ? val : null)
              }
            />
          </FormItem>

          {!readonly && !hideInternalSubmit && (
            <AntForm.Item {...tailLayout}>
              <Button
                id="button-save-business-partner-form"
                loading={loading}
                type="primary"
                htmlType="submit"
              >
                {loading
                  ? intlMessage({ id: 'saving' })
                  : intlMessage({ id: 'save' })}
              </Button>
            </AntForm.Item>
          )}
        </Form>
      )}
    </Formik>
  );
};
